/**
 * What a lead looks like to a component.
 *
 * Two things the API shape does not give directly and every screen needs:
 * `slaState`, because a raw due-date makes each agent do date arithmetic in
 * their head; and `assigneeName`, because the API carries a userId and a
 * possibly-absent hydrated user.
 */
export enum SlaState {
  /** Answered inside the target. */
  MET = 'met',
  /** Answered after the target, or unanswered past it. */
  BREACHED = 'breached',
  /** Unanswered, past the at-risk ratio but not yet due. */
  AT_RISK = 'at_risk',
  /** Unanswered and still comfortably inside the target. */
  AWAITING = 'awaiting',
  /** No target recorded — an older lead, or one created before SLAs existed. */
  UNKNOWN = 'unknown'
}

export interface LeadUiModel {
  id: string;
  shortId: number;
  vertical: string;
  details: Record<string, unknown>;
  /** Names for the catalogue ids on `details`, when the BFF could resolve them. */
  packageName?: string;
  modelName?: string;
  modelColor?: string;
  unitTypeGroupName?: string;
  /** Covers `propertyType` too — acquisition's name for the same catalogue. */
  typeOfUnitName?: string;
  areaLabel?: string;
  addonNames?: string[];

  name: string;
  phone: string;
  email: string;
  message: string;

  source: string;
  status: string;
  resolutionType?: string;
  notQualifiedReason?: string;
  notInterestedReason?: string;
  pendingReason?: string;
  resolutionNote?: string;
  resolvedAt?: Date;

  marketingConsent: boolean;
  consentSource?: string;
  consentAt?: Date;

  marketingChannel?: string;
  messagingChannel?: string;
  adType?: string;
  campaignSource?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmCampaignId?: string;
  utmTerm?: string;
  utmContent?: string;
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
  landingPath?: string;
  referrer?: string;
  attributedAt?: Date;
  firstTouchChannel?: string;
  firstTouchCampaign?: string;
  firstTouchSource?: string;
  firstTouchMedium?: string;
  firstTouchLandingPath?: string;
  firstTouchReferrer?: string;
  firstTouchAt?: Date;

  assignedToId?: string;
  /** The assignee's name, or their raw id when hydration could not resolve it. */
  assigneeName?: string;
  assignedAt?: Date;
  assignedByName?: string;

  contactAttempts: number;
  remainingContactAttempts: number;
  maxContactAttempts: number;
  contactAttemptLog: LeadContactAttemptUiModel[];

  slaState: SlaState;
  slaDueAt?: Date;
  firstResponseAt?: Date;

  qualityScore?: number;
  valueCategory?: string;
  scoredAt?: Date;

  duplicateOfLeadId?: string;
  idempotencyKey?: string;
  /**
   * Where a lead came from in human terms, derived from `idempotencyKey`.
   * A furnishing-sourced lead is the only one with a key today.
   */
  originLabel?: string;

  createdByName?: string;
  lastUpdatedByName?: string;

  lastComment?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface LeadContactAttemptUiModel {
  attemptNo: number;
  at: Date;
  /** The caller's name, or their raw id when hydration could not resolve it. */
  byName?: string;
  note?: string;
}

export interface LeadActivityUiModel {
  id: string;
  activityType: string;
  description: string;
  /** Absent means the system did it — an auto-resolution or a stale sweep. */
  actorName?: string;
  isSystem: boolean;
  createdAt: Date;
}

export interface LeadCommentUiModel {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
}
